class ChazokuSubscription extends HTMLElement {
  connectedCallback() {
    // The product <form> (the "buttons" block) renders AFTER this element in
    // the DOM, so it may not exist yet when the element first upgrades. Retry
    // until it does instead of silently bailing (which left the size buttons
    // and every other control inert).
    this.form = document.getElementById(this.dataset.formId);
    if (!this.form) {
      if (this._formRetries === undefined) this._formRetries = 0;
      if (this._formRetries < 60) {
        this._formRetries += 1;
        requestAnimationFrame(() => this.connectedCallback());
      } else if (document.readyState !== 'complete') {
        window.addEventListener('load', () => this.connectedCallback(), {once: true});
      }
      return;
    }
    if (this._booted) return;
    this._booted = true;

    this.purchaseInputs = [...this.querySelectorAll('[data-chazoku-purchase]')];
    this.frequencyButtons = [...this.querySelectorAll('[data-chazoku-frequency]')];
    this.sizeButtons = [...this.querySelectorAll('[data-chazoku-size]')];
    this.subscribeOptions = this.querySelector('[data-chazoku-subscribe-options]');
    this.planInput = this.querySelector('[data-chazoku-selling-plan]');
    this.propertyInputs = [...this.querySelectorAll('[data-chazoku-subscription-property]')];
    this.quantityInput = this.form.querySelector('input[name="quantity"]');
    this.quantityButtons = [...this.form.querySelectorAll('button[name="increase"], button[name="decrease"], [data-popout-toggle]')];
    this.addButton = this.form.querySelector('[data-add-to-cart]');
    this.addButtonText = this.form.querySelector('[data-add-to-cart-text]');
    this.originalButtonText = this.addButtonText?.textContent?.trim() || 'Add to cart';
    this.oneTimeQuantity = this.quantityInput?.value || '1';
    this.wasSubscribing = false;

    // The canonical <input name="id"> the product form submits to /cart/add.
    // It lives OUTSIDE the <form> (sections/product.liquid) and is attached via
    // form="...", so form.querySelector() can't see it — form.elements can.
    this.formVariantInputs = [...this.form.elements].filter((el) => el.name === 'id');
    this.formVariantInput = this.formVariantInputs[0] || null;
    // Main price plus the sticky cart-bar price, scoped to this product section.
    const section = this.closest('.shopify-section') || document;
    this.pageProductPrices = [...section.querySelectorAll('[data-product-price]')];

    // Swap the gallery's hero photo to match the selected size (the 35g and
    // 100g sizes are separate products/separate media, so this is a plain
    // src/srcset swap rather than Shopify's own media-id gallery switching).
    this.galleryImage = document.querySelector(
      '.product__slide.media--active .product__photo img, [data-product-media-list] .product__slide:first-child .product__photo img',
    );
    if (this.galleryImage) {
      this.galleryImageDefaults = {
        src: this.galleryImage.getAttribute('src'),
        srcset: this.galleryImage.getAttribute('srcset'),
        alt: this.galleryImage.getAttribute('alt'),
      };
    }

    // Size switching: keep both sizes' rendered data, swap without a reload.
    this.clientSideSize = this.dataset.sizeSwitchClientSide === 'true';
    this.pageSize = this.dataset.currentSize || '35g';
    this.currentSize = this.pageSize;
    this.sizeData = this.readSizeData();
    this.onetimeShippingNote = this.dataset.onetimeShippingNote || '+ Shipping';

    this.eligibilityEndpoint = this.dataset.eligibilityEndpoint?.trim();
    this.manualGiftEligible = this.dataset.giftsEligible !== 'false';
    this.manualEligibilityReason = this.dataset.giftEligibilityReason || 'not_configured';
    this.giftsEligible = this.eligibilityEndpoint ? false : this.manualGiftEligible;
    this.eligibilityReason = this.eligibilityEndpoint ? 'checking' : this.manualEligibilityReason;
    this.subscriptionAddHandler = (event) => this.addSubscriptionWithGifts(event);
    this.crossProductAddHandler = (event) => this.addCrossProductOneTime(event);
    this.addButton?.addEventListener('click', this.subscriptionAddHandler, true);
    this.addButton?.addEventListener('click', this.crossProductAddHandler, true);

    this.purchaseInputs.forEach((input) => {
      input.addEventListener('change', () => this.update());
    });

    this.frequencyButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.selectFrequency(button);
        this.update();
      });
    });

    this.sizeButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectSize(button.dataset.chazokuSize));
    });

    // Bubble-phase on the form runs after the theme's per-input listeners, so a
    // quantity +/- (which resets the id in the theme) is corrected right away.
    this.formChangeHandler = () => {
      if (this.currentSize !== this.pageSize) this.syncSizeVariant();
    };
    this.form.addEventListener('change', this.formChangeHandler);

    this.preorderRadioHandler = (event) => {
      if (!event.target.matches('.restock-rocket-payment-widget input[name="preorder-option"]')) return;
      if (this.selectedPurchase() === 'subscribe') return;
      this.restoreOneTimeButtonText();
    };
    document.addEventListener('change', this.preorderRadioHandler);

    const params = new URLSearchParams(window.location.search);
    const requestedPurchase = params.get('purchase');
    const requestedFrequency = params.get('frequency');
    const requestedSize = params.get('size');
    if (this.clientSideSize && requestedSize && requestedSize !== this.currentSize && this.sizeData[requestedSize]) {
      this.applySize(requestedSize);
    }
    const subscribeInput = this.purchaseInputs.find((input) => input.value === 'subscribe' && !input.disabled);
    if (requestedPurchase === 'subscribe' && subscribeInput) subscribeInput.checked = true;
    if (requestedFrequency === '90') {
      const ninety = this.frequencyButtons.find((button) => button.dataset.frequency === '90');
      if (ninety) this.selectFrequency(ninety);
    }

    this.update();
    this.checkGiftEligibility();
  }

  // ---- Size switching -------------------------------------------------------

  readSizeData() {
    const data = {};
    ['35', '100'].forEach((token) => {
      const p = `size${token}`;
      if (this.dataset[`${p}Available`] !== 'true') return;
      const key = token === '35' ? '35g' : '100g';
      data[key] = {
        url: this.dataset[`${p}Url`],
        variantId: this.dataset[`${p}VariantId`],
        onetime: this.dataset[`${p}Onetime`],
        image: this.dataset[`${p}Image`],
        imageSrcset: this.dataset[`${p}ImageSrcset`],
        imageAlt: this.dataset[`${p}ImageAlt`],
        monthly: {
          planId: this.dataset[`${p}MonthlyPlanId`],
          quantity: this.dataset[`${p}MonthlyQty`],
          retail: this.dataset[`${p}MonthlyRetail`],
          first: this.dataset[`${p}MonthlyFirst`],
          renewal: this.dataset[`${p}MonthlyRenewal`],
          save: this.dataset[`${p}MonthlySave`],
          firstPct: this.dataset[`${p}MonthlyFirstPct`],
          renewalPct: this.dataset[`${p}MonthlyRenewalPct`],
        },
        ninety: {
          planId: this.dataset[`${p}NinetyPlanId`],
          quantity: this.dataset[`${p}NinetyQty`],
          retail: this.dataset[`${p}NinetyRetail`],
          first: this.dataset[`${p}NinetyFirst`],
          renewal: this.dataset[`${p}NinetyRenewal`],
          save: this.dataset[`${p}NinetySave`],
          firstPct: this.dataset[`${p}NinetyFirstPct`],
          renewalPct: this.dataset[`${p}NinetyRenewalPct`],
        },
      };
    });
    return data;
  }

  selectSize(size) {
    if (!this.clientSideSize || size === this.currentSize || !this.sizeData[size]) return;
    this.applySize(size);
    // Keep the URL shareable/back-button friendly without navigating.
    // 10/22: size taps no longer rewrite the URL (incoming ?size= still preselects).
    this.update();
  }

  // Rewrites every size-dependent value in place. No navigation.
  applySize(size) {
    const data = this.sizeData[size];
    if (!data) return;
    this.currentSize = size;
    this.setAttribute('data-current-size', size);

    this.sizeButtons.forEach((button) => {
      const active = button.dataset.chazokuSize === size;
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-checked', active ? 'true' : 'false');
    });

    const sizeLabel = this.querySelector('[data-chazoku-size-label]');
    if (sizeLabel) sizeLabel.textContent = size;

    // Retarget the cart form + the page price to the sibling product variant.
    this.syncSizeVariant();

    // One-time price copy.
    this.querySelectorAll('[data-chazoku-onetime-price], [data-chazoku-onetime-note-price]')
      .forEach((el) => { el.textContent = data.onetime; });

    this.applyGalleryImage(size, data);

    // Rewrite the frequency buttons for this size.
    this.frequencyButtons.forEach((button) => {
      const plan = button.dataset.frequency === '90' ? data.ninety : data.monthly;
      button.dataset.planId = plan.planId || '';
      button.dataset.quantity = plan.quantity;
      button.dataset.retail = plan.retail;
      button.dataset.first = plan.first;
      button.dataset.renewal = plan.renewal;
      if (plan.save) button.dataset.save = plan.save;
      if (plan.firstPct) button.dataset.firstDiscount = plan.firstPct;
      if (plan.renewalPct) button.dataset.renewalDiscount = plan.renewalPct;
      button.querySelectorAll('[data-chazoku-frequency-qty]').forEach((el) => { el.textContent = plan.quantity; });
      button.querySelectorAll('[data-chazoku-frequency-size]').forEach((el) => { el.textContent = size; });
      const disable = !plan.planId;
      button.disabled = disable;
      button.classList.toggle('is-unavailable', disable);
    });

    // If the active frequency lost its plan for this size, fall back to monthly.
    const active = this.selectedFrequency();
    if (active && active.disabled) {
      const monthly = this.frequencyButtons.find((b) => b.dataset.frequency === '30');
      if (monthly) this.selectFrequency(monthly);
    }
  }

  // The theme's ProductFormReader resets <input name="id"> to THIS page's
  // variant on every quantity/plan/property change event (_setIdInputValue),
  // which silently reverted a 100g selection back to 35g. Re-apply the chosen
  // size's variant + price after any such event and right before add to cart.
  syncSizeVariant() {
    const data = this.sizeData?.[this.currentSize];
    if (!data?.variantId) return;
    this.formVariantInputs.forEach((input) => {
      if (input.value !== data.variantId) input.value = data.variantId;
    });
    const holder = this.querySelector('[data-chazoku-variant-id]');
    if (holder) holder.value = data.variantId;
    this.pageProductPrices.forEach((el) => { el.textContent = data.onetime; });
  }

  // Swaps the gallery hero photo to the selected size's product image. Falls
  // back to the page's own original photo when that size IS the page's
  // product (nothing to swap to) or when no image was supplied for it.
  applyGalleryImage(size, data) {
    if (!this.galleryImage) return;

    if (size === this.pageSize) {
      if (this.galleryImageDefaults) {
        this.galleryImage.setAttribute('src', this.galleryImageDefaults.src);
        if (this.galleryImageDefaults.srcset) this.galleryImage.setAttribute('srcset', this.galleryImageDefaults.srcset);
        if (this.galleryImageDefaults.alt) this.galleryImage.setAttribute('alt', this.galleryImageDefaults.alt);
      }
      return;
    }

    if (data.image) {
      this.galleryImage.setAttribute('src', data.image);
      if (data.imageSrcset) this.galleryImage.setAttribute('srcset', data.imageSrcset);
      if (data.imageAlt) this.galleryImage.setAttribute('alt', data.imageAlt);
    }
  }

  // ---- Gift eligibility ----------------------------------------------------

  async checkGiftEligibility() {
    if (!this.eligibilityEndpoint) return;

    try {
      const response = await fetch(this.eligibilityEndpoint, {
        credentials: 'same-origin',
        headers: {Accept: 'application/json'},
      });
      const result = await response.json().catch(() => ({}));
      this.giftsEligible = response.ok && result.eligible === true;
      this.eligibilityReason = result.reason || result.error || 'not_eligible';
    } catch (error) {
      this.giftsEligible = false;
      this.eligibilityReason = 'eligibility_check_unavailable';
    }

    this.update();
  }

  selectedPurchase() {
    return this.purchaseInputs.find((input) => input.checked)?.value || 'onetime';
  }

  selectedFrequency() {
    return this.frequencyButtons.find((button) => button.classList.contains('is-selected')) || this.frequencyButtons[0];
  }

  selectFrequency(selected) {
    this.frequencyButtons.forEach((button) => {
      const active = button === selected;
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-checked', active ? 'true' : 'false');
    });
  }

  setProperty(selector, value) {
    const input = this.querySelector(selector);
    if (input) input.value = value;
  }

  currentVariantId() {
    return this.formVariantInput?.value ||
      this.querySelector('[data-chazoku-variant-id]')?.value ||
      new FormData(this.form).get('id');
  }

  // Parses the merchant-configured gift list (up to 4 slots set in the theme
  // editor) from the data-gifts JSON attribute. Empty slots are already
  // excluded server-side, so this is just the gifts that actually exist.
  configuredGifts() {
    if (this._configuredGifts) return this._configuredGifts;
    try {
      this._configuredGifts = JSON.parse(this.dataset.gifts || '[]');
    } catch (error) {
      this._configuredGifts = [];
    }
    return this._configuredGifts;
  }

  // One-time add when the chosen size is a DIFFERENT product than this page.
  // The theme's normal add path serializes the form, and we have already
  // retargeted <input name="id"> to the sibling variant, so a plain add works —
  // this handler only needs to run when we are NOT subscribing.
  addCrossProductOneTime(event) {
    if (this.selectedPurchase() === 'subscribe') return;
    // Runs in the capture phase, before the theme's own add-to-cart click
    // handler serializes the form, so the id is guaranteed to be the chosen size.
    if (this.currentSize !== this.pageSize) this.syncSizeVariant();
  }

  addSubscriptionWithGifts(event) {
    if (this.selectedPurchase() !== 'subscribe' || !document.querySelector('cart-items')) return;

    const frequency = this.selectedFrequency();
    const variantId = this.currentVariantId();
    const sellingPlan = this.planInput.value;
    if (!frequency || !variantId || !sellingPlan) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const formData = new FormData(this.form);
    const properties = {};
    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    const bundleId = window.crypto && typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    properties['_Chazoku subscription parent'] = 'true';
    properties['_Chazoku subscription bundle'] = bundleId;

    const giftProperties = (name) => ({
      '_Chazoku subscription gift': 'true',
      '_Chazoku subscription bundle': bundleId,
      '_Gift name': name,
    });

    const items = [
      {
        id: variantId,
        quantity: frequency.dataset.quantity,
        sellingPlan,
        properties,
      },
    ];

    if (this.giftsEligible && this.dataset.giftsEnabled !== 'false') {
      this.configuredGifts()
        .filter((gift) => {
          return gift.frequency === 'both' ||
            (gift.frequency === 'monthly' && frequency.dataset.frequency === '30') ||
            (gift.frequency === 'ninety' && frequency.dataset.frequency === '90');
        })
        .forEach((gift) => {
          items.push({
            id: gift.variantId,
            quantity: 1,
            properties: giftProperties(gift.name),
          });
        });
    }

    const originalInputs = [...this.form.elements].filter((input) => {
      if (!input.name) return false;
      return input.name === 'id' ||
        input.name === 'quantity' ||
        input.name === 'selling_plan' ||
        input.name.startsWith('properties[');
    });
    const originalStates = originalInputs.map((input) => input.disabled);
    originalInputs.forEach((input) => {
      input.disabled = true;
    });

    const temporaryInputs = [];
    const addInput = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = String(value);
      input.dataset.chazokuTemporaryCartInput = '';
      this.form.appendChild(input);
      temporaryInputs.push(input);
    };

    items.forEach((item, index) => {
      addInput(`items[${index}][id]`, item.id);
      addInput(`items[${index}][quantity]`, item.quantity);
      if (item.sellingPlan) addInput(`items[${index}][selling_plan]`, item.sellingPlan);
      Object.entries(item.properties).forEach(([key, value]) => {
        addInput(`items[${index}][properties][${key}]`, value);
      });
    });

    try {
      document.dispatchEvent(
        new CustomEvent('theme:cart:add', {
          detail: {
            button: this.addButton,
          },
          bubbles: false,
        }),
      );
    } finally {
      temporaryInputs.forEach((input) => input.remove());
      originalInputs.forEach((input, index) => {
        input.disabled = originalStates[index];
      });
    }
  }

  disconnectedCallback() {
    this.addButton?.removeEventListener('click', this.subscriptionAddHandler, true);
    this.addButton?.removeEventListener('click', this.crossProductAddHandler, true);
    document.removeEventListener('change', this.preorderRadioHandler);
    this.form?.removeEventListener('change', this.formChangeHandler);
  }

  // Restock Rocket (the preorder app) drives its own "Pre-Order" vs. "Buy
  // now" radio (input[name="preorder-option"] inside
  // .restock-rocket-payment-widget) and rewrites the add-to-cart button text
  // to match. This element blindly overwrote that text with a stale snapshot
  // whenever switching back from Subscribe to one-time purchase. Instead,
  // ask Restock Rocket's own selection what the button should say.
  restoreOneTimeButtonText() {
    if (!this.addButtonText) return;

    const preorderRadio = document.querySelector(
      '.restock-rocket-payment-widget input[name="preorder-option"]:checked',
    );
    if (preorderRadio) {
      const label = preorderRadio.closest('.restock-rocket-payment-option')
        ?.querySelector('.restock-rocket-payment-title');
      this.addButtonText.textContent = label?.textContent?.trim() || 'Pre-Order';
      return;
    }

    this.addButtonText.textContent = this.originalButtonText;
  }

  update() {
    const subscribing = this.selectedPurchase() === 'subscribe';
    const frequency = this.selectedFrequency();
    if (!frequency) return;

    this.subscribeOptions.hidden = !subscribing;
    this.planInput.disabled = !subscribing;
    this.planInput.value = subscribing ? frequency.dataset.planId : '';
    this.propertyInputs.forEach((input) => {
      input.disabled = !subscribing;
    });

    const onetimeNote = this.querySelector('[data-chazoku-onetime-note]');
    if (onetimeNote) onetimeNote.hidden = subscribing;

    if (subscribing) {
      this.setProperty('[data-chazoku-property-size]', this.currentSize);
      this.setProperty('[data-chazoku-property-quantity]', frequency.dataset.quantity);
      this.setProperty('[data-chazoku-property-frequency]', frequency.dataset.frequencyLabel);
      this.setProperty('[data-chazoku-property-first-discount]', frequency.dataset.firstDiscount);
      this.setProperty('[data-chazoku-property-renewal-discount]', frequency.dataset.renewalDiscount);
      this.setProperty('[data-chazoku-property-gifts]', this.giftsEligible ? frequency.dataset.gifts : 'None');
      const setText = (sel, val) => { const el = this.querySelector(sel); if (el && val !== undefined) el.textContent = val; };
      setText('[data-chazoku-save]', frequency.dataset.save);
      setText('[data-chazoku-cadence]', frequency.dataset.cadence);
      this.querySelector('[data-chazoku-retail]').textContent = frequency.dataset.retail;
      this.querySelector('[data-chazoku-first]').textContent = frequency.dataset.first;
      const perUnit = this.querySelector('[data-chazoku-per-unit]');
      if (perUnit) perUnit.textContent = `/ ${frequency.dataset.quantity} ${frequency.dataset.quantity === '1' ? 'Tin' : 'Tins'}`;
      this.querySelector('[data-chazoku-renewal]').textContent = frequency.dataset.renewal;
      this.querySelector('[data-chazoku-first-discount]').textContent = frequency.dataset.firstDiscount;
      this.querySelector('[data-chazoku-renewal-discount]').textContent = frequency.dataset.renewalDiscount;
      this.querySelector('[data-chazoku-gifts]').textContent = this.giftsEligible ? frequency.dataset.gifts : 'No first-order gifts';
      const eligibilityStatus = this.querySelector('[data-chazoku-gift-eligibility]');
      if (eligibilityStatus) {
        if (this.eligibilityReason === 'checking') {
          eligibilityStatus.textContent = 'Checking first-order gift eligibility…';
        } else if (this.eligibilityReason === 'customer_login_required') {
          eligibilityStatus.textContent = 'Sign in before adding to cart to verify first-order gifts.';
        } else if (this.eligibilityReason === 'customer_tagged_gift_used') {
          eligibilityStatus.textContent = 'First-order gifts were already used on this account.';
        } else if (!this.giftsEligible && this.eligibilityEndpoint) {
          eligibilityStatus.textContent = 'First-order gifts are not available for this account.';
        } else {
          eligibilityStatus.textContent = '';
        }
      }
      const giftGrid = this.querySelector('[data-chazoku-gift-grid]');
      if (giftGrid && this.dataset.giftsEnabled !== 'false') {
        giftGrid.hidden = !this.giftsEligible;

        // Each gift slot declares which frequency it applies to
        // (data-chazoku-gift-frequency: "both" | "monthly" | "ninety"). Show
        // only the slots that match the selected frequency.
        const slots = [...giftGrid.querySelectorAll('[data-chazoku-gift-slot]')];
        let visibleCount = 0;
        slots.forEach((slot) => {
          const target = slot.dataset.chazokuGiftFrequency;
          const matches = target === 'both' ||
            (target === 'monthly' && frequency.dataset.frequency === '30') ||
            (target === 'ninety' && frequency.dataset.frequency === '90');
          slot.hidden = !this.giftsEligible || !matches;
          if (!slot.hidden) visibleCount += 1;
        });
        giftGrid.classList.toggle('chazoku-subscription__gift-grid--three', visibleCount >= 3);
      }
    }

    if (this.quantityInput) {
      if (subscribing) {
        if (!this.wasSubscribing) {
          this.oneTimeQuantity = this.quantityInput.value || this.oneTimeQuantity;
        }
        this.quantityInput.value = frequency.dataset.quantity;
      } else {
        this.quantityInput.value = this.oneTimeQuantity || '1';
      }
      this.quantityInput.readOnly = subscribing;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.quantityButtons.forEach((control) => {
      control.disabled = subscribing;
      control.setAttribute('aria-disabled', subscribing ? 'true' : 'false');
    });

    const popoutText = this.form.querySelector('[data-popout-toggle-text]');
    if (popoutText) {
      popoutText.textContent = subscribing ? frequency.dataset.quantity : (this.oneTimeQuantity || '1');
    }
    if (this.addButtonText) {
      if (subscribing) {
        this.addButtonText.textContent = 'Start subscription';
      } else {
        this.restoreOneTimeButtonText();
      }
    }
    this.form.classList.toggle('chazoku-form--subscription', subscribing);
    // Shipping message outside the widget follows the selected purchase option.
    document.querySelectorAll('[data-cz-shipmsg]').forEach((el) => {
      const text = subscribing ? el.dataset.sub : el.dataset.onetime;
      if (text && el.textContent !== text) el.textContent = text;
    });
    this.planInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.wasSubscribing = subscribing;
    // The change events above make the theme reset the id to this page's variant.
    if (this.currentSize !== this.pageSize) this.syncSizeVariant();
  }
}

if (!customElements.get('chazoku-subscription')) {
  customElements.define('chazoku-subscription', ChazokuSubscription);
}

// Live shipping line, computed in America/Chicago time (not the visitor's clock).
(() => {
  const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const chicagoNow = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago', weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value || '';
    return {day: DAYS.indexOf(get('weekday').toLowerCase().slice(0, 3)), hour: +get('hour'), minute: +get('minute')};
  };
  const render = (el) => {
    const cutoff = parseInt(el.dataset.cutoff, 10) || 12;
    const off = (el.dataset.offDays || '').toLowerCase().split(',').map((d) => d.trim().slice(0, 3)).filter(Boolean);
    const isShipDay = (d) => !off.includes(DAYS[d]);
    const now = chicagoNow();
    if (now.day < 0) return;
    const label = `${cutoff % 12 || 12} ${cutoff < 12 ? 'AM' : 'PM'} CT`;
    if (isShipDay(now.day) && now.hour < cutoff) {
      const mins = (cutoff - now.hour) * 60 - now.minute;
      const left = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
      el.textContent = `Order by ${label}, ships today (${left} left)`;
      return;
    }
    for (let i = 1; i <= 7; i += 1) {
      const d = (now.day + i) % 7;
      if (isShipDay(d)) {
        el.textContent = i === 1 ? 'Order now, ships tomorrow' : `Order now, ships ${NAMES[d]}`;
        return;
      }
    }
  };
  const run = () => document.querySelectorAll('[data-cz-ship]').forEach(render);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  setInterval(run, 60000);
})();
